import { Box, Stack } from '@mui/material';
import { TPerson, TValidationResults } from '@verifime/api-definition';
import {
  DisplayErrors,
  GenerateFormFields_New,
  generateNonFieldErrors,
  getFieldsRules_New,
  showFieldsErrors,
  TErrors,
  TFormFieldsAndRules_New,
  useCustomForm,
} from '@verifime/components';
import tokens from '@verifime/design-tokens';
import {
  Australia,
  COUNTRY_STATES,
  getStateItemsForCountry,
  isAustralianLicence,
  isDifferentCountryOfIssue,
  isNewZealandLicence,
  reportPageActionToNewRelic,
  TIdentityDocumentType,
  validationRules,
} from '@verifime/utils';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { TOcrData } from '../types';
import { buildOcrData } from '../utils';
import { expiryStatusOfDocumentFields, withExpiredDocumentRule } from './expiredDocumentRule';
import { getFormFieldConfigMap } from './formFieldConfigs';
import { checkIsEmptyForm, ocrTypeToDataKey } from './utils';

type OcrEditorProps = Readonly<{
  dataTestid?: string;
  countryCode: TPerson['address']['countryCode'];
  initialData: TOcrData;
  documentType: TIdentityDocumentType;
  /**
   * Whether every organisation of the customer accepts an expired document of this type. When it
   * does not, an expired expiry date fails the form (VME-4254). Required, so no caller can leave
   * the block off by forgetting it.
   */
  expiredDocumentAccepted: boolean;
  validationResults: TValidationResults;
  onFormChange?: (isValid: boolean, data: TOcrData) => void;
}>;

// isAustralianLicence, isDifferentCountryOfIssue and isNewZealandLicence used to be defined
// here, and separately again in the admin driver licence editor (a third copy lived in the
// legacy IdentityDocumentsWizard editor; that tree has been deleted). The NZ/NZL fault in
// isDifferentCountryOfIssue was found once and had to be fixed in all three copies that
// existed at the time, which is why the two that remain now both import from
// @verifime/utils.

export default function OcrEditor({
  dataTestid,
  countryCode,
  initialData,
  documentType,
  expiredDocumentAccepted,
  validationResults,
  onFormChange,
}: OcrEditorProps) {
  const initialLicenceCountry =
    documentType === 'DriverLicence'
      ? initialData?.driverLicence?.licenceCountryOfIssue || countryCode || ''
      : '';
  const [selectedLicenceCountry, setSelectedLicenceCountry] =
    useState<string>(initialLicenceCountry);

  const formFieldsAndRules = useMemo(
    () =>
      generateFormFieldsAndRulesWithLogic(
        documentType,
        selectedLicenceCountry,
        expiredDocumentAccepted,
      ),
    [documentType, selectedLicenceCountry, expiredDocumentAccepted],
  );

  const documentData = useMemo(() => {
    const dataKey = ocrTypeToDataKey[documentType];
    const data = cloneDeep((initialData?.[dataKey] as Record<string, any>) || {});
    if (documentType === 'DriverLicence' && !data.licenceCountryOfIssue && countryCode) {
      data.licenceCountryOfIssue = countryCode;
    }
    // A photo card is always Australian: the country is fixed, not read from the scan or from
    // where the customer lives, and the form does not let the customer choose it. None of the
    // other driver licence branches in this file applies to it: its State list is always the
    // Australian one, and it has no card number and no New Zealand version number to clear.
    if (documentType === 'PhotoCard') {
      data.photoCardCountryOfIssue = Australia.code;
    }
    return data;
  }, [initialData, documentType, countryCode]);

  const methods = useCustomForm<Record<string, any>>({
    defaultValues: documentData || {},
    schema: getFieldsRules_New(formFieldsAndRules),
  });
  const { setValue, getValues, trigger, watch, updateSchema, setError, schema } = methods;
  const isSyncInitialedRef = useRef(false);
  // The number of the most recent report to start. A report is a check of the whole form followed
  // by a call to `onFormChange`, and the check takes time, so a slow one can finish after a later
  // edit has been reported. Each path below drops its result when a later report has started, so an
  // older text can never be published over a newer one (and then written back into the input).
  const latestReportRef = useRef(0);
  // Every value this editor has reported. The parent hands the value it was given back as
  // `initialData`, after the customer may have typed more. That echo is the editor's own text, not
  // a value from outside, so it is not written into the form (it would put the older text back and
  // lose the characters typed since).
  const reportedDataRef = useRef(new WeakSet<object>());

  // The document type whose expired block has been reported and has not cleared since, so the
  // page action goes out once per block and not on every keystroke or re-render while blocked.
  const reportedExpiredBlockRef = useRef<TIdentityDocumentType | null>(null);

  const [nonFieldErrors, setNonFieldErrors] = useState<TErrors>([]);

  useEffect(() => {
    if (validationResults?.length < 1) {
      return;
    }
    showFieldsErrors(validationResults, setError);
    const nonFieldErrors = generateNonFieldErrors(validationResults);
    setNonFieldErrors(nonFieldErrors);
  }, [validationResults, setError]);

  useEffect(() => {
    updateSchema(getFieldsRules_New(formFieldsAndRules), { resetSchema: true });
  }, [formFieldsAndRules, updateSchema]);
  // Tells New Relic that a customer was blocked by an expired document (VME-4254): the document
  // type only, never an id or a date. The block is judged by the same check the form's rule runs.
  // A block counts as cleared only when the expiry is a real, current date. A blank or half-typed
  // date says nothing either way, so retyping an expired date digit by digit is still one block.
  const reportExpiredBlockOnce = useCallback(
    (data: TOcrData) => {
      const status = expiryStatusOfDocumentFields(
        documentType,
        data?.[ocrTypeToDataKey[documentType]] as Record<string, string | Date> | undefined,
        expiredDocumentAccepted,
        new Date(),
      );

      if (status === 'current') {
        reportedExpiredBlockRef.current = null;
        return;
      }
      if (status === 'unknown' || reportedExpiredBlockRef.current === documentType) {
        return;
      }
      reportedExpiredBlockRef.current = documentType;
      reportPageActionToNewRelic('identity-document-expired-blocked', { documentType });
    },
    [documentType, expiredDocumentAccepted],
  );

  // Ideally use `useEffectEvent` from React 18, but it still in experimental for compatibility we use `useCallback`
  // to ensure the function is stable across renders.
  const onFormChangeEvent = useCallback(
    (valid: boolean, data: TOcrData) => {
      if (data) {
        reportedDataRef.current.add(data);
      }
      reportExpiredBlockOnce(data);
      onFormChange?.(valid, data);
    },
    [onFormChange, reportExpiredBlockOnce],
  );
  // Initialize form with data from customer.
  // This is to ensure that the form is populated with initial data when the component mounts.
  // This is important for the first render to show the correct data.
  // If the form is not initialized with data, it will show empty fields.
  // Sync initialData or scan results into form
  useEffect(() => {
    if (initialData && reportedDataRef.current.has(initialData)) {
      return;
    }
    if (Object.keys(documentData || {}).length < 1) {
      isSyncInitialedRef.current = true;
      return;
    }

    const currentValues = getValues();
    const diffEntries = Object.entries(documentData).filter(
      ([k, v]) => !isEqual(v, currentValues[k]),
    );

    if (diffEntries.length > 0 || !isSyncInitialedRef.current) {
      if (diffEntries.length > 0) {
        // Use shouldValidate: false during bulk sync to avoid premature validation
        diffEntries.forEach(([k, v]) => {
          setValue(k, v, { shouldValidate: false });
        });
      }
      isSyncInitialedRef.current = true;

      // After sync, report once. The two other `await trigger()` reporting paths below (the
      // watch callback and the schema-change effect) drop a result that a later report has
      // overtaken, as this one does.
      const report = async () => {
        const reportId = ++latestReportRef.current;
        const values = getValues();
        const valid = await trigger();
        if (reportId !== latestReportRef.current) return;
        onFormChangeEvent(valid, buildOcrData(documentType, values));
      };
      report();
    }
  }, [initialData, documentData, setValue, getValues, onFormChangeEvent, documentType, trigger]);

  // Watch for changes and update local state + report
  useEffect(() => {
    const subscription = watch(async (formData) => {
      if (!isSyncInitialedRef.current) return;

      // Update local country state if needed
      if (documentType === 'DriverLicence' && formData.licenceCountryOfIssue) {
        const newCountry = formData.licenceCountryOfIssue ?? '';
        if (newCountry !== selectedLicenceCountry) {
          setSelectedLicenceCountry(newCountry);
          // Note: The schema update and subsequent validation will be handled
          // by the effect below that is keyed on `schema`.
          return;
        }
      }

      // Regular field change reporting
      const reportId = ++latestReportRef.current;
      const isEmptyForm = checkIsEmptyForm(formData);
      let valid = false;
      if (!isEmptyForm) {
        valid = await trigger();
      }
      if (reportId !== latestReportRef.current) return;
      onFormChangeEvent(valid, buildOcrData(documentType, formData));
    });
    return () => subscription.unsubscribe();
  }, [documentType, selectedLicenceCountry, watch, trigger, onFormChangeEvent]);

  // Every field a driver licence carries that belongs to one country only is cleared as soon
  // as the licence stops being from that country: the State on a country that has none, the
  // Australian card number, the New Zealand version number. Hiding the input is not enough -
  // react-hook-form keeps the value of a field that is no longer rendered, and buildOcrData
  // sends the whole form, so a New Zealand licence was still submitting the Australian card
  // number the customer typed before they switched (VME-4061).
  //
  // The State branch writes an empty string rather than NOT_APPLICABLE_STATE_CODE. The "N/A"
  // marker only ever existed to satisfy a rule that required it, and that rule is no longer
  // reachable: a country with no states does not render the State field at all, so its rule
  // never enters the schema. Sending "N/A" to person-api as if it were a state was the
  // remaining reason it was still being written.
  // Holds the country the fields below were last reconciled to, so a country change can be told
  // apart from a mount or a data sync. The distinction matters only for the State: on a mount the
  // State that arrived with the document is the one OCR read, and clearing it would throw away
  // the answer the customer came here with.
  const previousLicenceCountryRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (documentType !== 'DriverLicence') return;

    const previousLicenceCountry = previousLicenceCountryRef.current;
    previousLicenceCountryRef.current = selectedLicenceCountry;
    const countryOfIssueChanged =
      previousLicenceCountry !== undefined &&
      isDifferentCountryOfIssue(previousLicenceCountry, selectedLicenceCountry ?? '');

    const hasStates =
      !!selectedLicenceCountry && Object.keys(COUNTRY_STATES).includes(selectedLicenceCountry);

    const clearIfPresent = (fieldName: string) => {
      if (getValues(fieldName)) {
        setValue(fieldName, '', { shouldValidate: true });
      }
    };

    if (hasStates) {
      // A State chosen under the old country never carries over, even when the new country has a
      // State with the same code. Australia's WA and the USA's WA are the same two characters
      // (constants.ts:181 and :230), so asking only whether the old code is valid in the new list
      // left an Australian WA licence switched to the USA silently reading Washington - a State
      // the customer never chose, submitted as if they had (VME-4061).
      const items = getStateItemsForCountry(selectedLicenceCountry);
      const currentState = getValues('licenceState');
      const isCurrentStateValid = items.some((item) => item.code === currentState);
      if (countryOfIssueChanged || !isCurrentStateValid) {
        setValue('licenceState', '', { shouldValidate: true });
      }
    } else {
      clearIfPresent('licenceState');
    }

    if (!isAustralianLicence(selectedLicenceCountry)) {
      clearIfPresent('licenceCardNumber');
    }
    if (!isNewZealandLicence(selectedLicenceCountry)) {
      clearIfPresent('licenceVersionNumber');
    }
  }, [selectedLicenceCountry, documentType, getValues, setValue]);

  // Report back whenever the form's rules change, so that after a change of country of issue the
  // parent is told the new validity - and the fields the new country brings with it are validated,
  // which is what puts Required on an empty mandatory one (VME-4061).
  //
  // Keyed on `schema` rather than on formFieldsAndRules: updateSchema sets React state, so a change
  // of country lands in two renders - the first carries the new fields but still the old resolver,
  // the second carries the new one. Validating on the first checked the previous country's rules.
  useEffect(() => {
    if (!isSyncInitialedRef.current) return;

    const report = async () => {
      const reportId = ++latestReportRef.current;
      const values = getValues();
      const valid = await trigger();
      if (reportId !== latestReportRef.current) return;
      onFormChangeEvent(valid, buildOcrData(documentType, values));
    };
    report();
  }, [schema, trigger, getValues, onFormChangeEvent, documentType]);

  return (
    <Stack gap={tokens.spacingBase} data-testid={dataTestid} data-component="OcrEditor">
      <FormProvider {...methods}>
        <Stack>
          <GenerateFormFields_New
            formFieldsAndRules={formFieldsAndRules}
            control={methods.control}
            errors={methods.formState.errors}
            formFieldsWithDefaultValues={documentData}
          />
          {nonFieldErrors.length > 0 && (
            <Box
              data-testid={`${dataTestid}-non-field-errors`}
              data-component="OcrEditorNonField-Errors"
            >
              <DisplayErrors errors={nonFieldErrors} />
            </Box>
          )}
        </Stack>
      </FormProvider>
    </Stack>
  );
}

// This is a temporary solution, ideally we no need this logic.
// That means that once we render form according to the selected country,
// then form validaiton can be handled in specific document form editor natually.
function generateFormFieldsAndRulesWithLogic(
  documentType: TIdentityDocumentType,
  licenceCountryOfIssue: string | undefined,
  expiredDocumentAccepted: boolean,
) {
  const formFieldConfigMap = getFormFieldConfigMap();
  // The wizard-local expired rule goes on the expiry field before the rows are built, so the
  // driver licence branch below carries it along (VME-4254).
  const formFields = withExpiredDocumentRule(
    documentType,
    formFieldConfigMap[documentType],
    expiredDocumentAccepted,
  );

  // Convert form fields to row-based layout
  const result = Object.values(formFields).reduce(
    (acc, field, index) => ({
      ...acc,
      [`row${index}`]: [field],
    }),
    {} as TFormFieldsAndRules_New,
  );

  if (documentType === 'DriverLicence') {
    const formFieldsAndRules = cloneDeep(result);
    const driverLicenceFormFields = formFieldConfigMap.DriverLicence;
    const licenceCardNumber = driverLicenceFormFields.licenceCardNumber.fieldName;
    const licenceState = driverLicenceFormFields.licenceState.fieldName;
    const licenceVersionNumber = driverLicenceFormFields.licenceVersionNumber.fieldName;
    const hasStates =
      !!licenceCountryOfIssue && Object.keys(COUNTRY_STATES).includes(licenceCountryOfIssue);

    // Process fields: update state items or remove state field
    const keysToRemove: string[] = [];
    Object.entries(formFieldsAndRules).forEach(([key, fieldAndRules]) => {
      fieldAndRules.forEach((fieldAndRule) => {
        // Hide state field when country has no states
        if (fieldAndRule.fieldName === licenceState) {
          if (hasStates) {
            fieldAndRule.items = getStateItemsForCountry(licenceCountryOfIssue ?? '');
          } else {
            keysToRemove.push(key);
          }
        }

        // The version number is printed on New Zealand licences and no others. person-api
        // documents licenceVersionNumber as "NZ only", so every other country drops the row
        // rather than showing an input nothing on the licence answers (VME-4061). The test
        // is the country that issued the licence, not where the customer lives.
        if (fieldAndRule.fieldName === licenceVersionNumber) {
          if (isNewZealandLicence(licenceCountryOfIssue)) {
            fieldAndRule.rules =
              validationRules.REQUIRED_DRIVER_LICENCE_VERSION_NUMBER_VALIDATION_RULES;
          } else {
            keysToRemove.push(key);
          }
        }

        // The card number is printed on Australian licences and no others, so the test is
        // the country that issued the licence rather than the country the customer lives
        // in. Reading countryCode put "Licence Card Number (AU)" on a New Zealand licence
        // and, for an Australian resident, made it mandatory - so the likeliest thing a New
        // Zealand licence holder could do was type their version number into it (VME-4061).
        if (fieldAndRule.fieldName === licenceCardNumber) {
          if (isAustralianLicence(licenceCountryOfIssue)) {
            fieldAndRule.rules =
              validationRules.REQUIRED_DRIVER_LICENCE_CARD_NUMBER_VALIDATION_RULES;
          } else {
            keysToRemove.push(key);
          }
        }
      });
    });

    // Remove the rows whose only field does not apply to this licence
    keysToRemove.forEach((key) => delete formFieldsAndRules[key]);

    return formFieldsAndRules;
  }

  return result;
}
